import { App, Button, Divider, Input, InputNumber, Modal, Select, Spin, Table } from "antd";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { cancelManageHistoryColumns, cancelManageMemeColumns, getNextAvailableStatusForCancelManage, IProgramBookingInfo, useGetProgramBookingInfoByProgramBookingId } from "./model";
import { useEffect, useState } from "react";
import { EProgramBookingStatus, programBookingStatusToTextMap, textToProgramBookingStatusMap, useUpdateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest } from "../../../wellnessProgram/program/booking/model";
import { cancelTossPay } from "../../../../service/tossPay";
import { createProgramBookingMemoByCreateProgramBookingMemoAdminRequest } from "../../../../service/programBooking";

const CancelManageDetail = () => {
    const id = Number(useParams().id)
    const { data, isFetching, refetch } = useGetProgramBookingInfoByProgramBookingId<IProgramBookingInfo>(id)
    const [programBookingStatus, setProgramBookingStatus] = useState<EProgramBookingStatus>();
    const [changeStatusOpen, setChangeStatusOpen] = useState<boolean>(false);
    const [refundOpen, setRefundOpen] = useState<boolean>(false);
    const [refundConfirmOpen, setRefundConfirmOpen] = useState<boolean>(false);
    const [cancelAmount, setCancelAmount] = useState<number>();
    const [cancelReason, setCancelReason] = useState<string>();
    const [loading, setLoading] = useState<boolean>(false);
    const [memo, setMemo] = useState<string>('');
    const { message } = App.useApp();
    const updateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest = useUpdateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest();

    const onClickChangeStatusConfirmBtn = () => {
        if (!programBookingStatus || !data?.status) return message.error('상태 값 선택은 필수 입니다.');
        if (programBookingStatus === (textToProgramBookingStatusMap[data?.status])) { message.error('기존 상태값과 변경할 상태값이 동일합니다.'); return setChangeStatusOpen(false); }
        updateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest.mutate({ programBookingIdList: [id], status: programBookingStatus },
            { onSuccess: (res) => { if (res.data.data.updateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest) { refetch(); initState(); } } })
    }

    const onClickRefundConfirmBtn = () => {
        if (!data?.tossPayId) return;
        if (!cancelAmount) return message.error('환불 금액을 입력해주세요.');
        setLoading(true)
        cancelTossPay({ tossPayId: data?.tossPayId, cancelAmount: cancelAmount, cancelReason: cancelReason })
            .then(res => {
                if (res.data) {
                    message.success('환불 성공');
                    refetch();
                    initState()
                }
            })
            .finally(() => { setLoading(false); setRefundConfirmOpen(false); setRefundOpen(false) })
    }

    const onClickCreateMemo = () => {
        const createProgramBookingMemoAdminRequest: ICreateProgramBookingMemoAdminRequest = { programBookingId: id, memo: memo }
        createProgramBookingMemoByCreateProgramBookingMemoAdminRequest(createProgramBookingMemoAdminRequest)
            .then(res => { if (res.data) { message.success('메세지 저장 성공'); refetch(); initState(); } })
            .catch(err => console.error('err', err))
    }

    const initState = () => {
        setChangeStatusOpen(false);
        setRefundOpen(false);
        setRefundConfirmOpen(false);
        setCancelAmount(undefined);
        setCancelReason(undefined);
        setMemo('')
        data && data.status && setProgramBookingStatus(textToProgramBookingStatusMap[data.status] ?? data.status)
    }

    useEffect(() => {
        if (!data) return;
        data.status && setProgramBookingStatus(textToProgramBookingStatusMap[data.status] ?? data.status)
    }, [data?.status])

    return <>
        <div style={{ marginBottom: '24px' }}>
            <div style={{ marginBottom: '10px', padding: '10px 20px', border: '1px solid #b6b6b6', borderRadius: '4px' }}>
                <div style={{ fontWeight: 700, fontSize: '18px', marginBottom: '24px' }}>
                    예약 정보
                </div>
                {isFetching
                    ? <Spin style={{ height: '224px' }} />
                    : <div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                이름
                            </div>
                            <div style={{ flex: 5 }}>
                                {data?.memberName}
                            </div>
                        </div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                프로그램
                            </div>
                            <div style={{ flex: 5 }}>
                                {data?.programTitle}
                            </div>
                        </div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                이용일
                            </div>
                            <div style={{ flex: 5 }}>
                                {dayjs(data?.targetDateTime).format('YYYY.MM.DD')}
                            </div>
                        </div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                이용 시간
                            </div>
                            <div style={{ flex: 5 }}>
                                {dayjs(data?.targetDateTime).format('HH시 mm분')}
                            </div>
                        </div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                선택 옵션
                            </div>
                            <div style={{ flex: 5 }}>
                                {data?.programBookingSelectOptionList?.map((q, idx) => <div key={idx}>{(idx + 1) + '. ' + q.programSelectOptionItemList.filter(w => q.programSelectOptionItemIdList.includes(Number(w.id))).map(e => e.name + ' ')}</div>)}
                                {data?.programBookingSubjectOptionList?.map((q, idx) => <div key={idx}>{((idx + 1) + data?.programBookingSelectOptionList.length) + '. ' + q.content}</div>)}
                            </div>
                        </div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                결제금액
                            </div>
                            <div style={{ flex: 5 }}>
                                {data?.totalAmount?.toLocaleString()}원
                            </div>
                        </div>
                        <div style={{ display: 'flex' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                결제일
                            </div>
                            <div style={{ flex: 5 }}>
                                {dayjs(data?.paidDateTime).format('YYYY.MM.DD HH:mm')}
                            </div>
                        </div>
                    </div>}
            </div>

            <div style={{ marginBottom: '10px', padding: '10px 20px', border: '1px solid #b6b6b6', borderRadius: '4px' }}>
                <div style={{ fontWeight: 700, fontSize: '18px', marginBottom: '24px' }}>
                    취소 요청 정보
                </div>
                {isFetching
                    ? <Spin style={{ height: '88px' }} />
                    : <div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                취소 요청일
                            </div>
                            <div style={{ flex: 5 }}>
                                {dayjs(data?.requestCancelDateTime).format('YYYY.MM.DD HH:mm')}
                            </div>
                        </div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                상태
                            </div>
                            <div style={{ flex: 5 }}>
                                <Select style={{ marginRight: '16px' }} value={programBookingStatus} onChange={q => setProgramBookingStatus(q)} options={getNextAvailableStatusForCancelManage(programBookingStatus).map(q => ({ label: programBookingStatusToTextMap[q] ?? q, value: textToProgramBookingStatusMap[q] ?? q }))} />
                                <Button type='primary' loading={updateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest.isPending} onClick={() => setChangeStatusOpen(true)}>상태 변경</Button>
                            </div>
                        </div>
                        <div style={{ display: 'flex' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                취소/환불 규정
                            </div>
                            <div style={{ flex: 5 }} dangerouslySetInnerHTML={{ __html: data?.programRefundNotiHtml ?? '' }}>
                            </div>
                        </div>
                    </div>}
            </div>

            <div style={{ marginBottom: '10px', padding: '10px 20px', border: '1px solid #b6b6b6', borderRadius: '4px' }}>
                <div style={{ fontWeight: 700, fontSize: '18px', marginBottom: '24px' }}>
                    환불 관리
                </div>
                {isFetching
                    ? <Spin style={{ height: '122px' }} />
                    : <div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                토스페이먼츠 주문 번호
                            </div>
                            <div style={{ flex: 5 }}>
                                {data?.orderId}
                            </div>
                        </div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                결제금액
                            </div>
                            <div style={{ flex: 5 }}>
                                {data?.paidAmount?.toLocaleString()}원
                            </div>
                        </div>
                        <div style={{ display: 'flex', marginBottom: '14px' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px', lineHeight: '24px' }}>
                                환불가능 금액
                            </div>
                            <div style={{ flex: 5, display: 'flex', gap: '16px' }}>
                                <div style={{ lineHeight: '24px' }}>
                                    {data?.availableRefundAmount?.toLocaleString()}원
                                </div>
                                <Button size='small' type='primary' onClick={() => setRefundOpen(true)}>환불하기</Button>
                            </div>
                        </div>
                        <div style={{ display: 'flex' }}>
                            <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                                환불사유
                            </div>
                            <div style={{ flex: 5 }}>
                                {data?.tossPayCancelList?.map((q, idx) => <div key={idx}>{(idx + 1) + '. ' + q.cancelReason}</div>) || '-'}
                            </div>
                        </div>
                    </div>}
            </div>
        </div>

        <div style={{ padding: '10px 0' }}>
            <div style={{ paddingBottom: '10px', fontWeight: 700, fontSize: '16px' }}>
                히스토리
            </div>
            <Table loading={isFetching} dataSource={data?.programBookingStatusHistoryList} rowKey={'id'} columns={cancelManageHistoryColumns} />
        </div>

        <div style={{ padding: '10px 0' }}>
            <div style={{ paddingBottom: '10px', fontWeight: 700, fontSize: '16px' }}>
                메모
            </div>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
                <Input.TextArea value={memo} onChange={q => setMemo(q.target.value)} style={{ width: '300px', height: '100px' }} />
                <Button type='primary' onClick={() => onClickCreateMemo()}>메모 추가</Button>
            </div>
            <Table loading={isFetching} dataSource={data?.programBookingMemoList} rowKey={'id'} columns={cancelManageMemeColumns} />
        </div>


        <Modal open={changeStatusOpen} onCancel={() => setChangeStatusOpen(false)} onOk={() => setChangeStatusOpen(false)} footer={false} >
            <div>
                <div>
                    상태를 변경하시겠습니끼?
                </div>
                <div style={{ fontWeight: 700, fontSize: '14px' }}>
                    환불 완료 상태 변경 후에는 상태값 복원이 <span style={{ color: 'red' }}>불가능</span> 합니다.
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'right', gap: '16px', marginTop: '24px' }}>
                <Button onClick={() => setChangeStatusOpen(false)}>취소</Button>
                <Button type='primary' loading={updateProgramBookingStatusAdminByUpdateProgramBookingStatusRequest.isPending} onClick={() => onClickChangeStatusConfirmBtn()}>변경</Button>
            </div>
        </Modal>


        <Modal open={refundOpen} onCancel={() => setRefundOpen(false)} onOk={() => setRefundOpen(false)} footer={false} >
            <div style={{ fontWeight: 700, fontSize: '18px' }}>
                환불 관리
            </div>
            <Divider />
            <div>
                <div style={{ marginBottom: '14px' }}>
                    <div style={{ marginBottom: '12px', fontWeight: 700, fontSize: '16px' }}>
                        환불금액
                    </div>
                    <InputNumber value={cancelAmount} onChange={q => setCancelAmount(q ? Number(q) ?? 0 : undefined)} suffix={'원'} formatter={q => q ? Number(q)?.toLocaleString() : '0'} max={data?.availableRefundAmount ?? 0} style={{ width: '100%' }} />
                </div>
                <div style={{ marginBottom: '14px' }}>
                    <div style={{ marginBottom: '12px', fontWeight: 700, fontSize: '16px' }}>
                        환불사유
                    </div>
                    <Input value={cancelReason} onChange={q => setCancelReason(q.target.value)} maxLength={50} style={{ width: '100%' }} />
                </div>
                <div>
                    <div style={{ display: 'flex', marginBottom: '14px' }}>
                        <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                            주문 번호
                        </div>
                        <div style={{ flex: 4 }}>
                            {data?.orderId}
                        </div>
                    </div>
                    <div style={{ display: 'flex', marginBottom: '14px' }}>
                        <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                            구매자
                        </div>
                        <div style={{ flex: 4 }}>
                            {data?.memberName}
                        </div>
                    </div>
                    <div style={{ display: 'flex' }}>
                        <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                            결제일시
                        </div>
                        <div style={{ flex: 4, marginBottom: '14px' }}>
                            {dayjs(data?.paidDateTime).format('YYYY.MM.DD HH:mm')}
                        </div>
                    </div>
                    <div style={{ display: 'flex', marginBottom: '14px' }}>
                        <div style={{ flex: 1, fontWeight: 700, fontSize: '16px' }}>
                            결제액
                        </div>
                        <div style={{ flex: 4 }}>
                            {data?.paidAmount?.toLocaleString()}원
                        </div>
                    </div>
                    {((data?.paidAmount ?? 0) - (data?.availableRefundAmount ?? 0)) !== 0 &&
                        <div>
                            <div style={{ display: 'flex', marginBottom: '14px' }}>
                                <div style={{ flex: 1, fontWeight: 700, fontSize: '16px', lineHeight: '24px' }}>
                                    {(data?.paidAmount ?? 0) - (data?.availableRefundAmount ?? 0) === data?.paidAmount ? '취소' : '부분취소'}
                                </div>
                                <div style={{ flex: 4, display: 'flex', gap: '16px' }}>
                                    <div style={{ lineHeight: '24px', color: 'red' }}>
                                        {((data?.paidAmount ?? 0) - (data?.availableRefundAmount ?? 0)).toLocaleString()}원
                                    </div>
                                </div>
                            </div>
                            <Divider />
                        </div>}
                    <div style={{ display: 'flex', marginBottom: '14px' }}>
                        <div style={{ flex: 1, fontWeight: 700, fontSize: '16px', lineHeight: '24px' }}>
                            취소가능액
                        </div>
                        <div style={{ flex: 4, display: 'flex', gap: '16px' }}>
                            <div style={{ lineHeight: '24px' }}>
                                {data?.availableRefundAmount?.toLocaleString()}원
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
                <Button onClick={() => setRefundOpen(false)}>닫기</Button>
                <Button type='primary' onClick={() => setRefundConfirmOpen(true)}>완료</Button>
            </div>
        </Modal>

        <Modal centered open={refundConfirmOpen} onCancel={() => setRefundConfirmOpen(false)} onOk={() => setRefundConfirmOpen(false)} footer={false} >
            <div>
                <div style={{ fontWeight: 700, fontSize: '18px' }}>
                    환불 진행
                </div>
                <div style={{ fontWeight: 700, fontSize: '14px' }}>
                    환불 진행 후에는 복구가 <span style={{ color: 'red' }}>불가능</span> 합니다.
                </div>
                <div style={{ fontWeight: 700, fontSize: '14px' }}>
                    환불 진행하시겠습니끼?
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'right', gap: '16px', marginTop: '24px' }}>
                <Button onClick={() => setRefundConfirmOpen(false)}>아니요</Button>
                <Button type='primary' loading={loading} onClick={() => onClickRefundConfirmBtn()}>예</Button>
            </div>
        </Modal>
    </>
}

export default CancelManageDetail;